import { api } from '../client'

export interface OrganizationApiKeys {
  anthropic?: string
  openai?: string
  ollama?: string
  awsAccessKeyId?: string
  awsSecretAccessKey?: string
  awsRegion?: string
  awsSessionToken?: string
  nominatim?: string
  arcgis?: string
  /** CARTO basemap key — public by design; on-prem override for /config.json's cartoApiKey. */
  carto?: string
}

export interface OrganizationSettings {
  defaultMapEngine?: 'openlayers' | 'arcgis'
  defaultBasemap?: string
  defaultExtent?: {
    minX: number
    minY: number
    maxX: number
    maxY: number
    spatialReference: number
  }
  allowPublicMaps?: boolean
  allowExternalServers?: boolean
  maxStorageMB?: number
  primaryColor?: string
  secondaryColor?: string
  logoUrl?: string
  apiKeys?: OrganizationApiKeys
  registrationMode?: 'open' | 'interest'
}

export interface OrganizationLicense {
  key: string
  type: string
  expiresAt: string
  maxUsers: {
    admin: number
    publisher: number
    viewer: number
  }
  features: string[]
  isValid?: boolean
  validatedAt?: string
}

export interface Organization {
  _id: string
  tenantId: string
  name: string
  description?: string
  settings: OrganizationSettings
  license?: OrganizationLicense
  status: string
  createdAt: string
  updatedAt: string
}

export interface UpdateOrganizationInput {
  name?: string
  description?: string
  settings?: Partial<OrganizationSettings>
}

export interface LicenseValidationResult {
  isValid: boolean
  type?: string
  maxUsers?: {
    admin: number
    publisher: number
    viewer: number
  }
  features?: string[]
  expiresAt?: string
  fingerprint?: string
  error?: string
}

export const organizationsApi = {
  get: () => api.get<Organization>('/organizations/me'),

  update: (input: UpdateOrganizationInput) => api.put<Organization>('/organizations/me', input),

  uploadLogo: async (file: File) => {
    const formData = new FormData()
    formData.append('logo', file)

    const response = await fetch('/api/organizations/logo', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('gisify_token')}`,
      },
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Upload failed')
    }

    return response.json() as Promise<{ data: { logoUrl: string } }>
  },

  validateLicense: (licenseKey: string) =>
    api.post<LicenseValidationResult>('/organizations/license/validate', { licenseKey }),

  activateLicense: (licenseKey: string) => api.post<Organization>('/organizations/license/activate', { licenseKey }),

  getFingerprint: () => api.get<{ fingerprint: string }>('/organizations/fingerprint'),
}
